import React, {
  createRef,
  PureComponent,
} from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import convertPlainTextToHTML from '../../../../lib/convertPlainTextToHTML';
import ThemeIcons from '../../../../theme/Icons';
import TableDataCell from './TableDataCell';

const StyledWrapper = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 2px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  border: 0px;
  border-radius: 0px;
  border-color: transparent;
  background-color: transparent;
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  padding: ${(props) => props.padding}px;
  resize: none;

  &:hover,
  &:focus,
  &:focus-visible {
    outline: unset;
    border: 1px solid transparent;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  background: transparent;

  ${({ disabled, primaryColor }) => !disabled &&
    `
    cursor: pointer;

    &:hover {
      color: ${primaryColor || 'blue'};
    }
  `}
`;

const StyledDisplayText = styled.div`
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'white-space: nowrap;' : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'overflow: hidden;' : '')}
  word-break: break-word;
  overflow-wrap: break-word;
  width: 100%;
`;

class TableFieldBarcodeCell extends PureComponent {
  static propTypes = {
    id: PropTypes.string,
    disabled: PropTypes.bool,
    focused: PropTypes.bool,
    rowId: PropTypes.string,
    columnId: PropTypes.string,
    cellKey: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    onCapture: PropTypes.func,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    width: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.state = { changes: props.value };
    this.textareaRef = createRef();
  }

  componentWillUnmount() {
    const {
      onChange,
      focused,
      value,
      rowId,
      columnId,
      columnIdentifier,
      onBlur,
    } = this.props;
    const { changes } = this.state;

    /**
     * DO NOT REMOVE
     *
     * When a component is unmounted the blur event doesn't get fired.
     * This ensure that inputs that rely on the blur event have their
     * changes saved even when react is unmounting them.
     *
     * See: https://github.com/zionlabs/Issues/issues/1205
     *
     */
    if (onChange && focused && changes !== value && onBlur) {
      onChange(rowId, columnId, changes);

      onBlur(rowId, columnId, columnIdentifier, {});
    }
  }

  componentDidMount() {
    this.adjustTextareaHeight();
  }

  componentDidUpdate(prevProps, prevState) {
    const { value } = this.props;
    const { changes } = this.state;

    if (prevProps.value !== value) {
      this.setState({ changes: value });
    }

    if (prevState.changes !== changes) {
      this.adjustTextareaHeight();
    }
  }

  adjustTextareaHeight = () => {
    if (this.textareaRef.current) {
      const el = this.textareaRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  render() {
    const {
      id,
      rowId,
      columnId,
      disabled,
      cellKey,
      focused,
      onFocus,
      onKeyPress,

      primaryColor,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,
      width,
    } = this.props;

    const { changes } = this.state;

    return focused && !disabled ? (
      <TableDataCell
        key={`${cellKey}-editable`}
        {...{
          focused,
          width,
          padding: 0,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          borderColor,
          borderWidth,
          backgroundColor,
          primaryColor,
        }}
      >
        <StyledWrapper>
          <StyledTextarea
            autoFocus
            ref={(e) => {
              this.textareaRef.current = e;
            }}
            rows={1}
            onKeyDown={(e) => {
              if (onKeyPress) onKeyPress(rowId, columnId, e);
            }}
            value={changes}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            {...{
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              padding,
            }}
          />
          <IconWrapper
            onClick={this.handleIconClick}
            {...{ primaryColor, disabled }}
          >
            <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
          </IconWrapper>
        </StyledWrapper>
      </TableDataCell>
    ) : (
      <TableDataCell
        key={`${cellKey}-preview`}
        disabled={disabled}
        data-testid={`${id}-${rowId}-${columnId}-text-cell`}
        handleCellRef={(e) => {
          /**
           * Used to make sure the rendered textarea cell
           * is the same size as the display text when
           * the text cell is focused.
           */
          if (e) {
            this.height = e.offsetHeight;
            this.width = e.offsetWidth;
          }
        }}
        onClick={() => {
          if (!disabled && onFocus) onFocus(rowId, columnId);
        }}
        {...{
          primaryColor,
          width,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          borderColor,
          borderWidth,
          backgroundColor,
          padding,
        }}
      >
        <StyledWrapper>
          <StyledDisplayText
            {...{ textOverflow }}
            dangerouslySetInnerHTML={{ __html: this.getInnerHtml() }}
          />
          {disabled ? null : (
            <IconWrapper
              onClick={this.handleIconClick}
              {...{ primaryColor, disabled }}
            >
              <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
            </IconWrapper>
          )}
        </StyledWrapper>
      </TableDataCell>
    );
  }

  handleChange = (e) => {
    this.setState({ changes: e.target.value });
  };

  handleFocus = (e) => {
    const { onFocus, rowId, columnId, columnIdentifier, columnType } =
      this.props;

    if (onFocus) {
      onFocus(rowId, columnId, columnIdentifier, columnType, {
        blur: () => {
          e.target.blur();
        },
      });
    }
  };

  handleBlur = () => {
    const { changes } = this.state;
    const { value, onBlur, onChange, rowId, columnId, columnIdentifier } =
      this.props;

    if (onChange && changes !== value) onChange(rowId, columnId, changes);
    if (onBlur) onBlur(rowId, columnId, columnIdentifier, {});
  };

  handleIconClick = async () => {
    const {
      onCapture,
      onFocus,
      onChange,
      rowId,
      columnId,
      columnIdentifier,
      columnType,
      disabled,
    } = this.props;

    if (disabled) return;

    const response = await onCapture();

    if (this.textareaRef.current) {
      this.textareaRef.current.focus();
    }

    this.setState({ changes: response }, () => {
      // Ensure state is updated before calling onChange
      const { changes } = this.state;
      const { value } = this.props;

      if (onChange && changes !== value) {
        onChange(rowId, columnId, changes);
      }
    });
  };

  getInnerHtml = () => {
    const { displayValue } = this.props;

    const parsedValue =
      displayValue && typeof displayValue !== 'string'
        ? displayValue.toString()
        : displayValue;

    return convertPlainTextToHTML(parsedValue);
  };
}

export default TableFieldBarcodeCell;
